/**
 * Timezone-correct month attribution (TOPP-3 / BUG-H1).
 *
 * `completed_at` and other timestamps are stored as UTC instants. Deriving a
 * month/year with the host-local `Date.getMonth()`/`getFullYear()` is wrong in
 * CI/UTC and once aggregation moves to Postgres. This helper converts any UTC
 * instant to the Oslo (Europe/Oslo) calendar month/year, DST-aware and
 * independent of the host timezone.
 *
 * Pure — no Supabase/React imports. This is the test oracle for month
 * attribution; the calc files in src/lib/*-calc.ts use it.
 */

// 'en-CA' yields ISO-style YYYY-MM-DD parts, which we read by `type`.
const OSLO_PARTS = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Oslo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Convert a UTC instant to the Oslo calendar date parts.
 * @param dateInput ISO string, epoch millis, or Date.
 * @returns `{ year, month, day }` where `month` is 1-12 and `day` is 1-31
 *   (Oslo local).
 */
export function osloDateParts(dateInput: string | number | Date): {
  year: number;
  month: number;
  day: number;
} {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  const parts = OSLO_PARTS.formatToParts(date);
  let year = NaN;
  let month = NaN;
  let day = NaN;
  for (const part of parts) {
    if (part.type === "year") year = Number(part.value);
    else if (part.type === "month") month = Number(part.value);
    else if (part.type === "day") day = Number(part.value);
  }
  return { year, month, day };
}

/**
 * Convert a UTC instant to the Oslo calendar month.
 * @param dateInput ISO string, epoch millis, or Date.
 * @returns `{ year, month }` where `month` is 1-12 (Oslo local).
 */
export function osloMonthKey(dateInput: string | number | Date): {
  year: number;
  month: number;
} {
  const { year, month } = osloDateParts(dateInput);
  return { year, month };
}

/**
 * Oslo-local calendar date as a `YYYY-MM-DD` string. Useful as a stable,
 * timezone-correct sort/compare key for closed/completed dates.
 */
export function osloDateString(dateInput: string | number | Date): string {
  const { year, month, day } = osloDateParts(dateInput);
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}
